import React from 'react';
import { Button, Fade, Stack, Typography, useTheme } from '@mui/material';
import { useInstantSearchDataGridContext } from '@/components/tables/InstantSearchDataGrid';
import { getSearchRecordPath } from '@/components/tables/InstantSearchDataGrid/data';
import { firstTwoCapsOrNumbers, getNameInitials } from '@/utils/constants';
import { usePreviewMode } from '@/hooks/usePreviewMode';
import { paths } from '@/app/routes';
import { ClientNameAndAvatar } from '../../ClientNameAndAvatar';
import FolioLink from '@/components/FolioLink';
import { getDealClientId } from '@/features/deals/helper';

const DataGridClientName = ({ row, objectType }) => {
	const theme = useTheme();
	const { openPreview, closePreview, previewId } = usePreviewMode();
	const { hoveredRowId } = useInstantSearchDataGridContext();

	const dealClientId = getDealClientId(row);

	const name = row.clientName ?? row.name;
	const fileMigrationInProgress = row.fileMigrationInProgress;
	const clientView = objectType === 'client';
	const isRelationshipCompany = objectType === 'relationshipCompany';
	const showButton = clientView && hoveredRowId === row.id && !fileMigrationInProgress;
	const clientPath =
		clientView || isRelationshipCompany
			? getSearchRecordPath(row)
			: dealClientId
				? `${paths.clients}/${dealClientId}`
				: null;

	const handlePreview = (e) => {
		e.stopPropagation();
		const hasPreview = previewId;
		if (hasPreview) closePreview();
		setTimeout(() => openPreview(row.id), hasPreview ? 200 : 1);
	};

	if (clientView || isRelationshipCompany) {
		const avatar = (
			<ClientNameAndAvatar
				title={`${name}  ${fileMigrationInProgress ? '(Migration in progress)' : ''}`.trim()}
				initials={getNameInitials(name)}
				colour={theme.palette.primary.main}
				titleSx={{
					fontWeight: 600,
					'&:hover': {
						color: theme.palette.primary.main,
						textDecoration: 'underline',
					},
				}}
			/>
		);

		return (
			<Stack direction='row' width='100%' justifyContent='space-between' alignItems='center'>
				{clientPath && !fileMigrationInProgress ? (
					<FolioLink
						to={clientPath}
						stopPropagation
						sx={{ minWidth: 0, overflow: 'hidden' }}
					>
						{avatar}
					</FolioLink>
				) : (
					avatar
				)}
				<Fade in={showButton} timeout={{ enter: 50, exit: 50 }}>
					<Button
						data-client-preview-trigger
						onClick={handlePreview}
						variant='contained'
						size='small'
						sx={{ minWidth: 'auto' }}
					>
						Preview
					</Button>
				</Fade>
			</Stack>
		);
	}
	const dealClientName = (
		<Stack direction='row' spacing={1} alignItems={'center'}>
			<ClientNameAndAvatar
				title={name ?? 'Client not found'}
				initials={firstTwoCapsOrNumbers(row.description ?? row.dealName ?? 'NA')}
				width={300}
				colour='inherit'
				sx={{
					'&:hover': {
						color: theme.palette.primary.main,
					},
				}}
			/>
			{fileMigrationInProgress && (
				<Typography color='text.primary'>{'(Migration in progress)'}</Typography>
			)}
		</Stack>
	);

	if (!fileMigrationInProgress && clientPath) {
		return (
			<FolioLink to={clientPath} stopPropagation sx={{ display: 'inline-block' }}>
				{dealClientName}
			</FolioLink>
		);
	}
	return <div style={{ display: 'inline-block' }}>{dealClientName}</div>;
};

export default DataGridClientName;
